import { Overlay, OverlayContainer, ViewportRuler } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { KeycloakWrapperService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  BaseCssProperties,
  cssToNgStyle,
  ProviderClass,
  UiZoneWidgetConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { OVERLAY_ACTIVE_TYPE, OverlayWidgetComponent } from '../overlay/overlay';
@Component({
  selector: 'finxone-web-frontend-image-widget',
  templateUrl: './image-widget.component.html',
  styleUrls: ['./image-widget.component.scss'],
  providers: [{ provide: OVERLAY_ACTIVE_TYPE, useValue: false }],
})
export class ImageWidgetComponent
  extends OverlayWidgetComponent<ImageWidgetComponent>
  implements OnInit, OnChanges
{
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public widgetConfig: UiZoneWidgetConfig;
  @Input() imgPath = '';
  @Input() showImage = false;
  @Input() showExpandView = false;
  public isZoomed = false;
  show = false;
  mergedCss: any = {};
  public navigationData: {
    url: string;
    role: string;
    zone: string;
  } = {
    url: '',
    role: '',
    zone: '',
  };

  @Select(RoleState.getRole)
  roles!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;
  constructor(
    configService: ConfigService,
    dataIn: ProviderClass<unknown>,
    private keycloakService: KeycloakWrapperService,
    private _router: Router,
    private store: Store,
    overlay: Overlay,
    injector: Injector,
    _viewportRuler: ViewportRuler,
    _platform: Platform,
    _overlayContainer: OverlayContainer,
  ) {
    super(
      dataIn,
      overlay,
      injector,
      ImageWidgetComponent,
      _viewportRuler,
      _platform,
      _overlayContainer,
      configService,
    );
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      this.widgetConfig = this.dataIn.widgetConfig;
    }
    // BaseComponent deps must be set unconditionally: goToNavigationLink
    // dispatches on parentStore for every click, but setPageUrlData (the only
    // other place these were assigned) early-returns for widgets with no role
    // attribute and is skipped entirely when showImage is set
    this.parentStore = store;
    this.parentRouter = _router;
    this.parentKeycloakService = keycloakService;
    this.roles.pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      this.roleInfo = roles;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imgPath'] && !changes['imgPath'].firstChange) {
      this.getImageStyle();
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.widgetConfig && !this.showImage) {
      this.setPageUrlData(this.dataIn);
      if (this.widgetConfig.attributes['field_attributes']?.length) {
        for (const field of this.widgetConfig.attributes['field_attributes']) {
          const imagePath = await this.configService.getImagePath(field.additional_config.id);
          this.imgPath = imagePath.path;
          this.show = this.widgetConfig.attributes['show'];
        }
      } else if (this.widgetConfig.attributes['widgetProperties']?.['imageAssetUrl']) {
        this.imgPath = this.widgetConfig.attributes['widgetProperties']?.['imageAssetUrl'];
        this.show = this.widgetConfig.attributes['show'];
      }
    }
    this.getImageStyle();
  }

  private getImageStyle() {
    // eslint-disable-next-line @typescript-eslint/ban-types
    const imageCss: Object = {
      'background-image': this.imgPath ? 'url(' + this.imgPath + ')' : '',
    };

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.widgetConfig.attributes, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
      BaseCssProperties.OBJECT_FIT,
      BaseCssProperties.BOX_SHADOW,
    ]);

    if (this.overlayActive) {
      this.contentStyle['height'] = this.height;
      this.contentStyle['width'] = this.width;
    }
    this.mergedCss = Object.assign(imageCss, this.contentStyle);
  }

  public get mergedOuterCss() {
    return { ...this.mergedCss, ...cssToNgStyle(this.dataIn.widgetConfig.attributes.outerCss ?? '') };
  }

  public setPageUrlData(dataIn: ProviderClass<any>) {
    let url: string | undefined;

    if (dataIn.attri?.role === undefined) {
      return;
    }

    if (dataIn.attri?.role !== 'no-role') {
      url = this.configService.getPageIndexPageUrl(
        dataIn.attri?.zone ?? '',
        dataIn.attri?.pageIndex,
        dataIn.attri?.role,
      );
    } else {
      url = this.configService.getPageIndexPageUrl(dataIn.attri?.zone ?? '', dataIn.attri?.pageIndex);
    }
    if (
      [
        APP_ZONES.ONBOARDING,
        APP_ZONES.VERIFICATION,
        APP_ZONES.ORGANISATION_ONBOARDING,
        APP_ZONES.ORGANISATION_VERIFICATION,
      ].includes(dataIn.attri?.zone)
    ) {
      this.navigationData.url = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}/${url}/${
        dataIn.attri?.role
      }/default`;
    } else if (url) {
      this.navigationData.url = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}/${url}`;
    } else {
      this.navigationData.url = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}`;
    }
    this.navigationData.role = dataIn.attri?.role;
    this.navigationData.zone = dataIn.attri?.zone;
    this.parentNavigationData = this.navigationData;
    this.parentRoleInfo = this.roleInfo;
    this.parentRouter = this._router;
    this.parentKeycloakService = this.keycloakService;

    this.navigationOption = dataIn.attri?.navigationOption;
    this.parentStore = this.store;
  }

  public toggleZoom(event: MouseEvent) {
    event.stopPropagation();
    this.isZoomed = !this.isZoomed;
    const element = document.getElementsByClassName('image-wrapper')[0] as HTMLElement;
    if (element) {
      if (this.isZoomed) {
        element.style.width = '200vw';
        element.style.height = '140vh';
      } else {
        element.style.width = '100%';
        element.style.height = '100%';
      }
    }
  }
}
